import mixpanel from "mixpanel-browser";
import type {
  AnalyticsEventProperties,
  AnalyticsIdentity,
  AnalyticsTrackOptions,
  AnalyticsWorkspace,
  BrowserProductAnalytics,
  ProductAnalyticsEventName,
} from "@yagni/shared";

export interface BrowserMixpanelOptions {
  token: string;
  environment: string;
}

export class BrowserMixpanelProvider implements BrowserProductAnalytics {
  private initialized = false;

  constructor(private readonly opts: BrowserMixpanelOptions) {
    try {
      mixpanel.init(this.opts.token, {
        // `track_pageview: false` — we fire our own Page Viewed events
        // with normalized paths instead of raw URLs with IDs.
        track_pageview: false,
        persistence: "localStorage",
        ignore_dnt: false,
      });
      mixpanel.register({
        environment: this.opts.environment,
        source: "web",
      });
      this.initialized = true;
    } catch (err) {
      console.warn("mixpanel init failed", err);
    }
  }

  track<E extends ProductAnalyticsEventName>(
    event: E,
    properties: AnalyticsEventProperties<E>,
    options: AnalyticsTrackOptions = {}
  ): void {
    if (!this.initialized) return;
    try {
      const props: Record<string, unknown> = { ...(properties as Record<string, unknown>) };
      if (options.groups?.workspace) props.workspace = options.groups.workspace;
      mixpanel.track(event, props);
    } catch (err) {
      console.warn("mixpanel track failed", err);
    }
  }

  identify(identity: AnalyticsIdentity, workspace?: AnalyticsWorkspace): void {
    if (!this.initialized) return;
    try {
      mixpanel.identify(identity.userId);
      const traits: Record<string, unknown> = {
        $email: identity.email,
        $name: identity.displayName,
        $created: identity.createdAt,
        is_internal: identity.isInternal,
      };
      if (workspace) {
        traits.workspace_id = workspace.workspaceId;
        traits.workspace_slug = workspace.slug;
        if (workspace.plan !== undefined) traits.workspace_plan = workspace.plan;
      }
      mixpanel.people.set(traits);
      mixpanel.register({
        user_id: identity.userId,
        is_internal: identity.isInternal,
        ...(workspace
          ? {
              workspace_id: workspace.workspaceId,
              workspace_slug: workspace.slug,
              ...(workspace.plan !== undefined
                ? { workspace_plan: workspace.plan }
                : {}),
            }
          : {}),
      });
      if (workspace) {
        mixpanel.set_group("workspace", workspace.workspaceId);
      }
    } catch (err) {
      console.warn("mixpanel identify failed", err);
    }
  }

  setGroup(workspace: AnalyticsWorkspace): void {
    if (!this.initialized) return;
    try {
      mixpanel.set_group("workspace", workspace.workspaceId);
      mixpanel.get_group("workspace", workspace.workspaceId).set({
        $name: workspace.name,
        slug: workspace.slug,
        plan: workspace.plan,
        $created: workspace.createdAt,
      });
    } catch (err) {
      console.warn("mixpanel setGroup failed", err);
    }
  }

  alias(anonymousId: string, userId: string): void {
    if (!this.initialized) return;
    try {
      mixpanel.alias(userId, anonymousId);
    } catch (err) {
      console.warn("mixpanel alias failed", err);
    }
  }

  async flush(): Promise<void> {
    // mixpanel-browser batches and uses sendBeacon; no explicit flush needed.
  }

  reset(): void {
    if (!this.initialized) return;
    try {
      mixpanel.reset();
      mixpanel.register({
        environment: this.opts.environment,
        source: "web",
      });
    } catch (err) {
      console.warn("mixpanel reset failed", err);
    }
  }
}
